import React from 'react';
import ReviewIcon from 'assets/images/riviewdicon.svg';
import { preparePayoutDetails } from '../../../../utils/tableUtils';
import { formatCurrency } from 'utils';
import type { AuditSummaryContext, ManualPayoutTileConfig, TileConfig } from '../types';
import { buildFreeScratchersSubtext, renderTitleWithInfoIcon, resolveVarianceBadge } from './shared';

export function buildPayoutsTile(ctx: AuditSummaryContext): TileConfig {
  if (!ctx.isAllShift) {
    return {
      id: 'payouts',
      visible: false,
      variant: 'standard_summary',
      title: 'PAYOUTS',
    };
  }

  if (ctx.manualDayEntryEditability.lotto) {
    const variance = ctx.dailyTotals?.reconciliation_variance?.payout_variance ?? 0;
    const posTotal = ctx.dailyTotals?.pos?.payout ?? 0;
    const posFreeScratchersAmount = ctx.dailyTotals?.pos?.scratcher_free_tickets_paid_amount ?? 0;
    const posFreeScratchersCount = ctx.dailyTotals?.pos?.scratcher_free_tickets_paid_count ?? 0;

    const config: ManualPayoutTileConfig = {
      id: 'payouts',
      visible: true,
      variant: 'manual_payouts',
      title: 'PAYOUTS',
      variance,
      posTotal,
      posFreeScratchersAmount,
      posFreeScratchersCount,
      cardClassName: ctx.mode === 'bothNotConnected' ? 'compact-tall-manual-card' : 'manual-entry-card',
      footer: ctx.mode === 'posOnly' ? `POS Total: ${formatCurrency(posTotal)}` : 'Manual Entry',
    };

    return config;
  }

  const payoutsInLotto = ctx.dailyTotals?.lotto?.payouts_total ?? 0;
  const payoutsInPOS = ctx.dailyTotals?.pos?.payout ?? 0;
  const payoutsVariance = ctx.dailyTotals?.reconciliation_variance?.payout_variance ?? 0;
  const posFreeScratchersAmount = ctx.dailyTotals?.pos?.scratcher_free_tickets_paid_amount ?? 0;
  const posFreeScratchersCount = ctx.dailyTotals?.pos?.scratcher_free_tickets_paid_count ?? 0;
  const freeTicketsPaid = ctx.dailyTotals?.lotto?.scratcher_free_tickets_paid_amount ?? 0;

  if (ctx.mode === 'bothConnected') {
    const hasPayoutInput = ctx.isPosValueMissing && ctx.posMissingPayoutInput !== '';
    const effectivePosPayout = hasPayoutInput
      ? Number(ctx.posMissingPayoutInput) || 0
      : payoutsInPOS;
    const effectivePayoutVariance = (payoutsInLotto + freeTicketsPaid) - effectivePosPayout;

    const badge = resolveVarianceBadge(effectivePayoutVariance);
    const hasNoVariance = effectivePayoutVariance === 0;

    const posLottoSales = ctx.dailyTotals?.pos?.lotto_sales ?? 0;
    const lottoSales = ctx.dailyTotals?.lotto?.sales ?? 0;
    const lottoPayoutsTotal = ctx.dailyTotals?.lotto?.payouts_total ?? 0;

    const isPosZero = posLottoSales === 0 && effectivePosPayout === 0;
    const isLottoZero = lottoSales === 0 && lottoPayoutsTotal === 0 && freeTicketsPaid === 0;

    const details = preparePayoutDetails(
      lottoPayoutsTotal,
      freeTicketsPaid,
      effectivePosPayout,
      effectivePayoutVariance,
      isPosZero,
      isLottoZero,
      true
    );

    return {
      id: 'payouts',
      visible: true,
      variant: 'standard_lotto_pos',
      title: renderTitleWithInfoIcon('PAYOUTS'),
      badge,
      value: hasNoVariance ? (
        <img
          src={ReviewIcon}
          alt="Review"
          className="review-icon"
          style={{ width: '32px', height: '32px' }}
        />
      ) : (
        formatCurrency(effectivePayoutVariance)
      ),
      subtext: buildFreeScratchersSubtext(posFreeScratchersAmount, posFreeScratchersCount),
      footer: `Total: ${formatCurrency(payoutsInLotto + freeTicketsPaid)}`,
      actionLabel: 'Acknowledged',
      cardClassName: ctx.isPosValueMissing ? 'pos-missing-input-card' : undefined,
      inputPlaceholder: ctx.isPosValueMissing ? 'Enter POS Payouts' : undefined,
      popover:
        typeof details === 'object' && details != null && 'calculations' in details
          ? { calculations: details.calculations, showZeroDiscrepancyPopover: true }
          : undefined,
    };
  }

  if (ctx.mode === 'posOnly') {
    return {
      id: 'payouts',
      visible: true,
      variant: 'standard_summary',
      title: 'PAYOUTS',
      value: formatCurrency(payoutsVariance),
      subtext: buildFreeScratchersSubtext(posFreeScratchersAmount, posFreeScratchersCount),
      footer: `POS Total: ${formatCurrency(payoutsInPOS)}`,
      actionLabel: 'Verified in Lottery',
    };
  }

  if (ctx.mode === 'lottoOnly') {
    const lottoFreeScratchersAmount = ctx.dailyTotals?.lotto?.scratcher_free_tickets_paid_amount ?? 0;
    const lottoFreeScratchersCount = ctx.dailyTotals?.lotto?.scratcher_free_tickets_paid_count ?? 0;

    return {
      id: 'payouts',
      visible: true,
      variant: 'standard_summary',
      title: 'PAYOUTS',
      value: formatCurrency(payoutsInLotto),
      subtext: buildFreeScratchersSubtext(lottoFreeScratchersAmount, lottoFreeScratchersCount),
      footer: 'Reported by Lottery',
      actionLabel: 'Verified in POS',
      cardClassName: 'compact-tall-summary-card',
    };
  }

  if (ctx.mode === 'bothNotConnected') {
    return {
      id: 'payouts',
      visible: true,
      variant: 'standard_summary',
      title: 'PAYOUTS',
      value: 'Match payouts reported by POS with payouts in lottery portal.',
      footer: '',
      actionLabel: 'Verified in both',
      className: 'centered-content',
      valueClassName: 'hardcoded-value',
    };
  }

  return {
    id: 'payouts',
    visible: true,
    variant: 'standard_summary',
    title: 'PAYOUTS',
    value: formatCurrency(payoutsInLotto),
    footer: 'Reported by Lottery',
    actionLabel: 'Mark Verified',
  };
}

